import {
  Box,
  Text,
  NativeSelect,
  Button,
  HStack,
  Badge,
  Wrap,
  WrapItem,
  useDisclosure,
} from '@chakra-ui/react';
import { FiEdit2, FiX } from 'react-icons/fi'
import { useContactLists } from '../../hooks/useContactList'
import { useListFilters } from '../../hooks/useAnalyticsMap'
import { useReadCaps } from '../../hooks/useLimits'
import { FilterEditModal } from './FilterEditModal'
import type { FilterGroup, FilterFieldMetadata, TagOption } from '../../types/contactList'
import { useState, useEffect } from 'react'
import { uuid } from '../../lib/uuid'

interface AnalyticsFilterPanelProps {
  filterGroups: FilterGroup[]
  onFilterGroupsChange: (groups: FilterGroup[]) => void
  fieldMetadata: FilterFieldMetadata[]
  tags: TagOption[]
  selectedListId: string | null
  onListIdChange: (listId: string | null) => void
}

export function AnalyticsFilterPanel({
  filterGroups,
  onFilterGroupsChange,
  fieldMetadata,
  tags,
  selectedListId,
  onListIdChange,
}: AnalyticsFilterPanelProps) {
  const { data: lists } = useContactLists()
  const { data: listFilters } = useListFilters(selectedListId)
  const [hasAppliedListFilters, setHasAppliedListFilters] = useState(false)
  const filterModal = useDisclosure()

  const caps = useReadCaps()
  const selectedList = lists?.find((l) => l.id === selectedListId)
  const selectedListCount = selectedList?.memberCount
  // Pre-flight only: the server cap is the gate. Unknown caps => no warning.
  const overMapCap =
    caps !== undefined &&
    selectedListCount !== undefined &&
    selectedListCount !== null &&
    selectedListCount > caps.maxDynamicListContacts

  // When list filters load, populate the filter groups
  useEffect(() => {
    if (listFilters && !hasAppliedListFilters) {
      const groups: FilterGroup[] = listFilters.filterGroups.map((fg) => ({
        groupIndex: fg.groupIndex,
        filters: fg.filters.map((f) => ({
          id: uuid(),
          fieldName: f.fieldName,
          operator: f.operator,
          value: f.value,
          displayOrder: f.displayOrder,
        })),
      }))
      onFilterGroupsChange(groups)
      setHasAppliedListFilters(true)
    }
  }, [listFilters, hasAppliedListFilters, onFilterGroupsChange])

  const handleListChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value || null
    onListIdChange(val)
    setHasAppliedListFilters(false)
  }

  const handleClearAll = () => {
    onFilterGroupsChange([])
    onListIdChange(null)
    setHasAppliedListFilters(false)
  }

  return (
    <Box>
      {/* List selector */}
      <Box mb={3}>
        <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" mb={1}>
          Load from list
        </Text>
        <NativeSelect.Root size="sm">
          <NativeSelect.Field
            placeholder="Select a contact list..."
            value={selectedListId ?? ''}
            onChange={handleListChange}>
            {lists?.map((list) => (
              <option key={list.id} value={list.id}>
                {list.label} ({list.memberCount ?? 0} contacts)
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
        {overMapCap && (
          <Text color="warning.fg" fontSize="xs" mt={1}>
            This list has {selectedListCount!.toLocaleString()} contacts; this server maps at most{' '}
            {caps!.maxDynamicListContacts.toLocaleString()} — materialize the list first, or narrow
            the filter.
          </Text>
        )}
      </Box>

      {/* Compact filter labels */}
      <HStack justify="space-between" mb={2}>
        <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase">
          Filters
        </Text>
        <HStack gap={1}>
          {filterGroups.length > 0 && (
            <Button size="xs" variant="ghost" colorPalette="red" onClick={handleClearAll}><FiX />Clear
                          </Button>
          )}
          <Button
            size="xs"
            variant="ghost"
            colorPalette="brand"
            onClick={filterModal.onOpen}><FiEdit2 />Edit
                      </Button>
        </HStack>
      </HStack>

      {filterGroups.length === 0 ? (
        <Text fontSize="xs" color="gray.400" fontStyle="italic">
          No filters applied
        </Text>
      ) : (
        <Box>
          {filterGroups.map((group, index) => (
            <Box key={group.groupIndex}>
              {index > 0 && (
                <Badge
                  colorPalette="orange"
                  fontSize="2xs"
                  mx="auto"
                  display="block"
                  textAlign="center"
                  my={1}
                  w="fit-content"
                >
                  OR
                </Badge>
              )}
              <Wrap gap={1}>
                {group.filters.map((filter, fi) => {
                  const field = fieldMetadata.find((m) => m.fieldName === filter.fieldName)
                  const label = field?.displayLabel ?? filter.fieldName
                  return (
                    <WrapItem key={filter.id ?? fi}>
                      {fi > 0 && (
                        <Text fontSize="2xs" color="gray.400" mr={1} lineHeight="20px">
                          AND
                        </Text>
                      )}
                      <Badge
                        variant="subtle"
                        colorPalette="gray"
                        fontSize="2xs"
                        fontWeight="normal"
                        px={1.5}
                        py={0.5}
                        borderRadius="md"
                      >
                        {label} {filter.operator.replace(/([A-Z])/g, ' $1').trim().toLowerCase()} {filter.value ?? ''}
                      </Badge>
                    </WrapItem>
                  );
                })}
              </Wrap>
            </Box>
          ))}
        </Box>
      )}

      {/* Filter edit modal */}
      <FilterEditModal
        isOpen={filterModal.open}
        onClose={filterModal.onClose}
        filterGroups={filterGroups}
        onApply={onFilterGroupsChange}
        fieldMetadata={fieldMetadata}
        tags={tags}
      />
    </Box>
  );
}
